import type { JobsApi } from '@/features/jobs/api/job-api'
import { Id } from '@/infra/id'
import {
  ListJobQuestionsRequest,
  MakeCorrectionsRequest,
} from '@taxfyle/api-internal/internal/job_pb'
import type { AnswersMap, Question } from '@/features/jobs/questions/questions'
import {
  mapQuestionProtoToQuestion,
  populateAnswerMapProto,
} from '@/features/jobs/questions/question-mappers'
import {
  BoolInputValue,
  InputValue,
} from '@taxfyle/api-internal/internal/question_input_value_pb'
import { retryOnRpcError } from '@/utils/grpc-util'

/**
 * Provides methods for querying and answering the job questionnaire.
 */
export class QuestionStore {
  constructor(private readonly _api: JobsApi) {
    this.fetchQuestions = this.fetchQuestions.bind(this)
  }

  /**
   * Fetches the questions for a job, as they follow from the given answers,
   * or from the job's own answers when none are given.
   *
   * @param query
   */
  async fetchQuestions(
    query: FetchQuestionsQuery,
  ): Promise<FetchQuestionsResult> {
    query.signal?.throwIfAborted()

    const request = new ListJobQuestionsRequest().setId(Id.assert(query.jobId))
    if (query.answers) {
      populateAnswerMapProto(query.answers, request.getAnswersMap())

      // The backend falls back to the job's saved answers when the map is
      // empty, so deselecting the only answer would bring it back.
      if (query.answers.size === 0) {
        request
          .getAnswersMap()
          .set(
            `WORK_PORTAL_PLACEHOLDER_${Date.now()}`,
            new InputValue().setBoolValue(new BoolInputValue().setValue(true)),
          )
      }
    }

    const response = await retryOnRpcError(
      () => this._api.jobs.listJobQuestions(request),
      { signal: query.signal },
    )

    return {
      intakeCompleted: response.getIntakeCompleted(),
      questions: response.getQuestionsList().map(mapQuestionProtoToQuestion),
    }
  }

  /**
   * Corrects the answers of a submitted job.
   *
   * @param jobId
   * @param answers
   * @param reason Why the answers are corrected.
   */
  async makeCorrections(jobId: Id, answers: AnswersMap, reason: string) {
    const request = new MakeCorrectionsRequest()
      .setId(Id.assert(jobId))
      .setReason(reason)
    populateAnswerMapProto(answers, request.getAnswersMap())

    await this._api.jobs.makeCorrections(request)
  }
}

/** @see {QuestionStore.fetchQuestions} */
export interface FetchQuestionsQuery {
  jobId: Id
  answers?: AnswersMap | null
  signal?: AbortSignal
}

/** @see {QuestionStore.fetchQuestions} */
export interface FetchQuestionsResult {
  intakeCompleted: boolean
  questions: Question[]
}
